if (!customElements.get('bfx-header')) {
  customElements.define(
    'bfx-header',
    class BfxHeader extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.bfxh__bar');
        this.spacer = this.parentElement && this.parentElement.querySelector('[data-bfxh-spacer]');
        this.drawer = this.querySelector('[data-drawer]');
        this.panels = Array.from(this.querySelectorAll('.bfxh-drawer__panel'));
        this.openItem = null;
        this.frame = null;

        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onScroll = this.onScroll.bind(this);

        this.bindNavigation();
        this.bindDrawer();
        this.bindAccordions();
        this.bindMarkets();

        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);

        this.measure();
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.measure());
          this.observer.observe(this.bar);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.observer) this.observer.disconnect();
      }

      get hoverTrigger() {
        return this.dataset.trigger === 'hover' && window.matchMedia('(pointer: fine)').matches;
      }

      reveal(scope) {
        if (!scope) return;
        scope.querySelectorAll('[data-src], [data-srcset]').forEach((node) => {
          if (node.dataset.srcset) {
            node.srcset = node.dataset.srcset;
            delete node.dataset.srcset;
          }
          if (node.dataset.src) {
            node.src = node.dataset.src;
            delete node.dataset.src;
          }
        });
      }

      measure() {
        const height = this.bar.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', height.toFixed(1) + 'px');
        if (this.spacer) this.spacer.style.height = height.toFixed(1) + 'px';
        if (!this.classList.contains('bfxh--stuck')) {
          this.stickAt = this.getBoundingClientRect().top + window.scrollY;
        }
        if (this.openItem) this.setOverlayOffset();
      }

      setOverlayOffset() {
        this.style.setProperty('--bfxh-below', Math.max(0, this.bar.getBoundingClientRect().bottom) + 'px');
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.openItem) this.setOverlayOffset();

          if (this.dataset.sticky !== 'true') return;
          const stuck = window.scrollY > (this.stickAt || 0);
          if (stuck === this.classList.contains('bfxh--stuck')) return;
          this.classList.toggle('bfxh--stuck', stuck);
          if (this.spacer) this.spacer.classList.toggle('is-active', stuck);
          if (this.openItem) this.setOverlayOffset();
        });
      }

      bindNavigation() {
        this.items = Array.from(this.querySelectorAll('.bfxh__item'));

        this.items.forEach((item) => {
          const toggle = item.querySelector('[data-mega-toggle]');
          const mega = toggle && this.querySelector('#' + toggle.getAttribute('aria-controls'));

          item.addEventListener('mouseenter', () => {
            if (!this.hoverTrigger) return;
            if (mega) this.openMega(item, mega, toggle);
            else this.closeMega();
          });

          if (!toggle || !mega) return;

          item.addEventListener('focusin', () => {
            let keyboard = false;
            try {
              keyboard = toggle.matches(':focus-visible');
            } catch (error) {
              keyboard = false;
            }
            if (keyboard) this.openMega(item, mega, toggle);
          });

          toggle.addEventListener('click', (event) => {
            if (this.hoverTrigger && toggle.dataset.url && toggle.dataset.url !== '#') {
              window.location.href = toggle.dataset.url;
              return;
            }
            event.preventDefault();
            if (item.classList.contains('is-open')) this.closeMega();
            else this.openMega(item, mega, toggle);
          });

          mega.querySelectorAll('[data-rail]').forEach((rail) => {
            const activate = () => {
              mega.querySelectorAll('[data-rail]').forEach((other) => {
                other.classList.toggle('is-active', other === rail);
              });
              mega.querySelectorAll('[data-panel]').forEach((panel) => {
                panel.hidden = panel.dataset.panel !== rail.dataset.rail;
              });
              this.reveal(mega.querySelector('[data-panel]:not([hidden])'));
            };
            rail.addEventListener('mouseenter', activate);
            rail.addEventListener('focus', activate);
          });
        });

        this.addEventListener('mouseleave', () => {
          if (this.hoverTrigger) this.closeMega();
        });

        const overlay = this.querySelector('[data-mega-overlay]');
        if (overlay) overlay.addEventListener('click', () => this.closeMega());
      }

      bindMarkets() {
        this.markets = Array.from(this.querySelectorAll('[data-market]'));

        this.markets.forEach((market) => {
          const toggle = market.querySelector('[data-market-toggle]');
          const list = toggle && this.querySelector('#' + toggle.getAttribute('aria-controls'));
          if (!toggle || !list) return;

          toggle.addEventListener('click', (event) => {
            event.preventDefault();
            const open = market.classList.contains('is-open');
            this.closeMarkets();
            if (open) return;
            this.closeMega();
            market.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            list.hidden = false;
          });
        });
      }

      closeMarkets() {
        if (!this.markets) return false;
        let closed = false;
        this.markets.forEach((market) => {
          if (!market.classList.contains('is-open')) return;
          const toggle = market.querySelector('[data-market-toggle]');
          const list = toggle && this.querySelector('#' + toggle.getAttribute('aria-controls'));
          market.classList.remove('is-open');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          if (list) list.hidden = true;
          closed = true;
        });
        return closed;
      }

      openMega(item, mega, toggle) {
        if (this.openItem === item) return;
        this.closeMarkets();
        this.closeMega();
        this.openItem = item;
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        mega.hidden = false;
        this.classList.add('is-mega-open');
        this.reveal(mega.querySelector('[data-panel]:not([hidden])'));
        this.setOverlayOffset();
      }

      closeMega() {
        if (!this.openItem) return;
        const toggle = this.openItem.querySelector('[data-mega-toggle]');
        const mega = toggle && this.querySelector('#' + toggle.getAttribute('aria-controls'));
        this.openItem.classList.remove('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        if (mega) mega.hidden = true;
        this.classList.remove('is-mega-open');
        this.openItem = null;
      }

      bindDrawer() {
        if (!this.drawer) return;

        this.querySelectorAll('[data-drawer-open]').forEach((button) => {
          button.addEventListener('click', () => this.openDrawer(button));
        });

        this.querySelectorAll('[data-drawer-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeDrawer());
        });

        this.querySelectorAll('[data-panel-open]').forEach((button) => {
          const panel = this.querySelector('#' + button.dataset.panelOpen);
          if (!panel) return;
          button.addEventListener('click', () => {
            panel.classList.add('is-open');
            panel.setAttribute('aria-hidden', 'false');
            this.reveal(panel.querySelector('.bfxh-drawer__cards:not([hidden])'));
            const back = panel.querySelector('[data-panel-close]');
            if (back) requestAnimationFrame(() => back.focus());
          });
        });

        this.querySelectorAll('[data-panel-close]').forEach((button) => {
          button.addEventListener('click', () => {
            const panel = button.closest('.bfxh-drawer__panel');
            if (!panel) return;
            panel.classList.remove('is-open');
            panel.setAttribute('aria-hidden', 'true');
          });
        });

        this.drawer
          .querySelectorAll('[aria-controls="SearchDrawer"], [aria-controls="CartDrawer"]')
          .forEach((link) => {
            link.addEventListener('click', () => this.closeDrawer());
          });
      }

      openDrawer(button) {
        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        if (button) button.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('bfxh-body-locked');
        this.closeMega();
        const close = this.drawer.querySelector('[data-drawer-close]');
        if (close) requestAnimationFrame(() => close.focus());
      }

      closeDrawer() {
        this.closeMarkets();
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.panels.forEach((panel) => {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
        });
        document.documentElement.classList.remove('bfxh-body-locked');
        this.querySelectorAll('[data-drawer-open]').forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });
      }

      bindAccordions() {
        this.querySelectorAll('[data-accordion]').forEach((button) => {
          const target = this.querySelector('#' + button.getAttribute('aria-controls'));
          if (!target) return;
          button.addEventListener('click', () => {
            const open = button.getAttribute('aria-expanded') === 'true';
            button.setAttribute('aria-expanded', open ? 'false' : 'true');
            target.hidden = open;
            if (!open) this.reveal(target);
            const entry = button.closest('.bfxh-drawer__entry');
            if (entry) entry.classList.toggle('is-open', !open);
          });
        });
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        if (this.closeMarkets()) return;
        if (this.drawer && this.drawer.classList.contains('is-open')) {
          const panel = this.panels.find((item) => item.classList.contains('is-open'));
          if (panel) {
            panel.classList.remove('is-open');
            panel.setAttribute('aria-hidden', 'true');
            return;
          }
          this.closeDrawer();
          return;
        }
        this.closeMega();
      }

      onDocumentClick(event) {
        if (!event.target.closest || !event.target.closest('[data-market]')) this.closeMarkets();

        const legacy = event.target.closest && event.target.closest('[aria-controls="MenuDrawer"]');
        if (legacy && this.drawer && !document.getElementById('MenuDrawer')) {
          event.preventDefault();
          this.openDrawer(legacy);
          return;
        }

        if (!this.openItem) return;
        if (this.contains(event.target)) return;
        this.closeMega();
      }
    }
  );
}
